import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api, { errMsg, fmtDate } from "../lib/api";
import { PageHeader, Empty } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Wrench, AlertTriangle, Clock, Activity, RefreshCw, Search, Star, Settings2 } from "lucide-react";

const STATUS_META = {
  running: { label: "Operando", cls: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: Activity, dot: "bg-emerald-500" },
  in_maintenance: { label: "En Mantenimiento", cls: "bg-blue-100 text-blue-800 border-blue-300", icon: Wrench, dot: "bg-blue-500" },
  pm_due: { label: "PM próximo", cls: "bg-amber-100 text-amber-800 border-amber-300", icon: Clock, dot: "bg-amber-500" },
  alert: { label: "Aviso abierto", cls: "bg-red-100 text-red-800 border-red-300", icon: AlertTriangle, dot: "bg-red-500" },
};

export default function Dispatch() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("all");
  const [area, setArea] = useState("all");
  const [q, setQ] = useState("");
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [favorites, setFavorites] = useState([]);
  const [favOpen, setFavOpen] = useState(false);
  const [onlyFavs, setOnlyFavs] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/mnt/dispatch");
      setData(d);
    } catch (e) { toast.error(errMsg(e)); }
  }, []);

  const loadFavs = useCallback(async () => {
    try {
      const { data } = await api.get("/mnt/dispatch/favorites");
      setFavorites(data.equipment_ids || []);
    } catch { setFavorites([]); }
  }, []);

  const saveFavs = async (newFavs) => {
    try {
      await api.put("/mnt/dispatch/favorites", { equipment_ids: newFavs });
      setFavorites(newFavs);
      toast.success("Equipos favoritos guardados");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const toggleFavorite = (eqId) => {
    const next = favorites.includes(eqId)
      ? favorites.filter((x) => x !== eqId)
      : [...favorites, eqId];
    saveFavs(next);
  };

  useEffect(() => { load(); loadFavs(); }, [load, loadFavs]);
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const filtered = (data?.equipments || []).filter((e) => {
    if (onlyFavs && favorites.length > 0 && !favorites.includes(e.id)) return false;
    if (status !== "all" && e.status !== status) return false;
    if (area !== "all" && e.area !== area) return false;
    if (q && !`${e.code} ${e.name} ${e.model}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  const areas = Array.from(new Set((data?.equipments || []).map((e) => e.area).filter(Boolean)));

  return (
    <div data-testid="dispatch-page">
      <PageHeader title="Dispatch de Flotilla" subtitle="Estado en vivo de todos los equipos activos con disparadores de OTs abiertas.">
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1 text-[10px] font-bold uppercase text-muted-foreground cursor-pointer" title="Filtrar por equipos favoritos guardados">
            <input type="checkbox" checked={onlyFavs} onChange={(e) => setOnlyFavs(e.target.checked)} data-testid="disp-only-favs" />
            <Star size={11} className="text-amber-500" /> Solo favoritos
          </label>
          <Button size="sm" variant="outline" onClick={() => setFavOpen(true)}
                  className="rounded-none text-xs font-bold h-8" data-testid="disp-manage-favs-btn">
            <Settings2 size={13} className="mr-1" /> Mis equipos ({favorites.length})
          </Button>
          <label className="flex items-center gap-1 text-[10px] font-bold uppercase text-muted-foreground cursor-pointer">
            <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} data-testid="disp-autorefresh" />
            Auto (30s)
          </label>
          <Button size="sm" variant="outline" onClick={load} className="rounded-none text-xs font-bold h-8" data-testid="disp-refresh">
            <RefreshCw size={13} className="mr-1" /> Actualizar
          </Button>
        </div>
      </PageHeader>

      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4" data-testid="disp-summary">
          {Object.keys(STATUS_META).map((k) => {
            const Icon = STATUS_META[k].icon;
            const count = data.counts[k] || 0;
            return (
              <button key={k} onClick={() => setStatus(status === k ? "all" : k)}
                className={`text-left border p-3 hover:opacity-80 ${status === k ? "ring-2 ring-primary" : ""}`}
                data-testid={`disp-summary-${k}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 ${STATUS_META[k].dot}`}></span>
                  <span className="text-[9px] uppercase tracking-widest text-muted-foreground font-bold">{STATUS_META[k].label}</span>
                </div>
                <div className="text-2xl font-black tabular-nums mt-1 flex items-center gap-2">
                  {count}
                  <Icon size={14} className="text-muted-foreground" />
                </div>
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center mb-3">
        <div className="flex-1 min-w-40 max-w-xs relative">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar equipo…"
                 className="rounded-none h-8 text-xs pl-7" data-testid="disp-search" />
        </div>
        <Select value={area} onValueChange={setArea}>
          <SelectTrigger className="rounded-none h-8 text-xs w-40" data-testid="disp-filter-area"><SelectValue placeholder="Área" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas las áreas</SelectItem>
            {areas.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="text-[10px] text-muted-foreground ml-auto">
          Actualizado: {data && fmtDate(data.generated_at)}
        </div>
      </div>

      {!data ? (<div className="text-xs text-muted-foreground">Cargando…</div>) :
        filtered.length === 0 ? (<Empty text="Sin coincidencias" />) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="disp-cards">
          {filtered.map((e) => {
            const meta = STATUS_META[e.status] || STATUS_META.running;
            return (
              <div key={e.id} className="border bg-card p-3" data-testid={`disp-eq-${e.code}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 ${meta.dot}`}></span>
                      <span className="text-sm font-black tabular-nums">{e.code}</span>
                    </div>
                    <div className="text-[11px] font-bold">{e.name}</div>
                    <div className="text-[10px] text-muted-foreground">{e.model} · {e.area || "—"}</div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 border ${meta.cls}`}>{meta.label}</span>
                </div>

                <div className="grid grid-cols-3 gap-1 mb-2 text-center">
                  <div className="border p-1">
                    <div className="text-[8px] uppercase text-muted-foreground font-bold">Horómetro</div>
                    <div className="text-sm font-black tabular-nums">{e.current_hourmeter.toFixed(0)} h</div>
                  </div>
                  <div className="border p-1">
                    <div className="text-[8px] uppercase text-muted-foreground font-bold">OTs abiertas</div>
                    <div className={`text-sm font-black tabular-nums ${e.open_wos_count > 0 ? "text-blue-700" : ""}`}>{e.open_wos_count}</div>
                  </div>
                  <div className="border p-1">
                    <div className="text-[8px] uppercase text-muted-foreground font-bold">Avisos</div>
                    <div className={`text-sm font-black tabular-nums ${e.open_notifs_count > 0 ? "text-red-700" : ""}`}>{e.open_notifs_count}</div>
                  </div>
                </div>

                {e.next_pm && (
                  <div className={`text-[10px] p-1.5 mb-2 ${e.next_pm.overdue ? "bg-red-100 text-red-800 border border-red-300" : e.next_pm.hours_to_go <= (data.hours_before || 50) ? "bg-amber-100 text-amber-800 border border-amber-300" : "bg-muted"}`}>
                    <b>Próximo PM:</b> {e.next_pm.template_code} · {e.next_pm.template_name}
                    <br/>
                    <b>Faltan:</b> <span className="tabular-nums">{e.next_pm.hours_to_go.toFixed(0)} h</span>
                    {" "}(a {e.next_pm.next_due_hourmeter.toFixed(0)} h)
                    {e.next_pm.overdue && <span className="ml-1 font-black">· VENCIDO</span>}
                  </div>
                )}

                {e.open_wos.length > 0 && (
                  <div className="mb-2">
                    <div className="text-[9px] font-bold uppercase text-muted-foreground mb-1">OTs abiertas</div>
                    {e.open_wos.slice(0, 3).map((w) => (
                      <button key={w.wo_id}
                        onClick={() => navigate(`/mnt/work-orders/${w.wo_id}`)}
                        className="w-full text-left text-[10px] p-1 border-l-2 border-blue-500 bg-blue-50 hover:bg-blue-100 mb-0.5"
                        data-testid={`disp-wo-${w.wo_number}`}>
                        <div className="flex justify-between">
                          <b>{w.wo_number}</b>
                          <span className="uppercase text-[9px] bg-white px-1">{w.status}</span>
                        </div>
                        <div className="text-[9px] text-muted-foreground truncate">{w.description}</div>
                      </button>
                    ))}
                    {e.open_wos.length > 3 && <div className="text-[9px] text-muted-foreground">+{e.open_wos.length - 3} más</div>}
                  </div>
                )}

                {e.open_notifs.length > 0 && (
                  <div>
                    <div className="text-[9px] font-bold uppercase text-muted-foreground mb-1">Avisos</div>
                    {e.open_notifs.slice(0, 3).map((n) => (
                      <button key={n.notif_id}
                        onClick={() => navigate(`/mnt/notifications`)}
                        className="w-full text-left text-[10px] p-1 border-l-2 border-red-500 bg-red-50 hover:bg-red-100 mb-0.5"
                        data-testid={`disp-notif-${n.notif_number}`}>
                        <div className="flex justify-between">
                          <b>{n.notif_number}</b>
                          <span className="uppercase text-[9px] bg-white px-1">{n.type}{n.pm_code && ` · ${n.pm_code}`}</span>
                        </div>
                        <div className="text-[9px] text-muted-foreground truncate">
                          {n.auto_generated && <span className="text-primary">[AUTO] </span>}{n.description}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Favorites manager dialog */}
      <Dialog open={favOpen} onOpenChange={setFavOpen}>
        <DialogContent className="rounded-none max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="disp-favs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 uppercase font-black">
              <Star size={16} className="text-amber-500" /> Mis equipos favoritos
            </DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-muted-foreground">
            Selecciona los equipos que quieres ver en tu Dispatch. Se guardan por usuario.
            {favorites.length === 0 && " Si no seleccionas ninguno, se muestran todos."}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 max-h-96 overflow-y-auto border p-2">
            {(data?.equipments || []).map((eq) => {
              const on = favorites.includes(eq.id);
              return (
                <label key={eq.id}
                       className={`flex items-center gap-2 p-2 text-xs cursor-pointer hover:bg-muted ${on ? "bg-amber-50 border-l-2 border-amber-500" : ""}`}
                       data-testid={`disp-fav-row-${eq.code}`}>
                  <input type="checkbox" checked={on} onChange={() => toggleFavorite(eq.id)}
                         data-testid={`disp-fav-toggle-${eq.code}`} />
                  <span className="font-bold">{eq.code}</span>
                  <span className="text-muted-foreground truncate">{eq.name}</span>
                </label>
              );
            })}
          </div>
          <div className="flex justify-between gap-2">
            <Button size="sm" variant="outline" onClick={() => saveFavs([])}
                    className="rounded-none text-xs" data-testid="disp-favs-clear">
              Limpiar selección
            </Button>
            <Button size="sm" onClick={() => setFavOpen(false)}
                    className="rounded-none text-xs font-bold" data-testid="disp-favs-close">
              Listo ({favorites.length})
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
